/**
 * **The guided path from "I just signed up" to "my systems are connected."** — F26's wizard.
 *
 * ── Why a wizard rather than the screens that already exist ───────────────────────────────────
 *
 * Every piece of this was reachable before: the tool inventory names the systems a client uses,
 * `Connect` lists the providers, `Sources` shows what is connected. What did not exist was the
 * *order*, and the order is the product: a person who has just signed up does not know that
 * SharePoint needs a Microsoft administrator until they have pressed Connect and been refused by
 * Entra with a message that names neither the condition nor who to ask.
 *
 * So this screen is one thing the other three are not: a sequence that states each prerequisite
 * **before** the button that depends on it.
 *
 * ── The four steps, and why they are these four ───────────────────────────────────────────────
 *
 *   1. **Which systems do you use?** — chosen, not typed. The answer decides everything after it.
 *   2. **What each one needs from you first** — the prerequisites, split by *who* must act. This is
 *      the step that does not exist anywhere else, and the reason the wizard is worth building.
 *   3. **Connect** — one OAuth round trip per system, resumable, because step 2 often ends in
 *      "ask your admin" and the person comes back tomorrow.
 *   4. **Confirm** — what is connected, and what happens next.
 *
 * ── ⚠️ What this screen deliberately does NOT ask for ─────────────────────────────────────────
 *
 * **A client id or a client secret.** Per [ADR-079](../../../docs/platform/11-technical-architecture.md#8--architecture-decision-records)
 * Dedupr registers one app per provider per environment and every client consents to *that* — so a
 * client never registers an app and never holds a credential. A field for one here would be a field
 * that puts a secret in a browser, and it would also be asking somebody to do work that is ours.
 *
 * ⚠️ **When our side is not ready, the wizard says so plainly and does not pretend otherwise.**
 * `can_connect_now` is false when the deployment has no app credentials for that provider; the step
 * then names it as ours rather than showing a button that fails.
 *
 * ── Resumability ──────────────────────────────────────────────────────────────────────────────
 *
 * The chosen systems persist to the control plane through F26's setup journey (`setup_journey`), so
 * closing the tab does not lose the answer and a colleague sees the same queue. The connect step
 * derives its state from the *connections that exist*, never from a stored "step 3 done" flag —
 * a stored step would drift from reality the first time somebody disconnected a source.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  EmptyState,
  HelpText,
  PageHeader,
  Skeleton,
  Stepper,
  WizardShell,
} from '@dedupr/ui';
import { accessToken } from '../auth.js';
import { startConnect } from '../connectFlow.js';
import './SetupWizard.css';

interface Prerequisite {
  title: string;
  detail: string;
  who: 'you' | 'admin';
  link?: string;
}

interface ProviderView {
  providerId: string;
  providerName: string;
  transport: string;
  can_connect_now: boolean;
  serverMissing: string[];
  /* ⚠️ All three OPTIONAL, and read defensively below. A published connector (ADR-084) carries no
     prerequisite entry, and an older deployment may not send these at all — neither is a reason to
     crash a wizard. A missing list means "nothing required", which is the honest reading. */
  client_prerequisites?: Prerequisite[];
  /** How to create your OWN application — a third list, distinct from the operator steps. */
  client_nothing_required?: boolean;
  our_limit?: string | null;
}

interface ConnectionView {
  id: string;
  provider: string;
  state: string;
}

const STEPS = ['Your systems', 'What they need', 'Connect', 'Done'];

export function SetupWizard({ orgId }: { orgId?: string }) {
  const [providers, setProviders] = useState<ProviderView[] | null>(null);
  const [connections, setConnections] = useState<ConnectionView[] | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [step, setStep] = useState(0);
  const [failed, setFailed] = useState<string | null>(null);

  /* Shared with `Connect` and `Integrations`, so a refusal here reads the same as it does there and
     none of the three can drift back into a top-level navigation. */
  const begin = useCallback(
    async (providerId: string) => {
      const outcome = await startConnect(providerId, { orgId });
      if (!outcome.ok) setFailed(outcome.detail);
    },
    [orgId]
  );

  const load = useCallback(async () => {
    setFailed(null);
    try {
      const setup = (await (await fetch('/api/relay/setup')).json()) as { providers: ProviderView[] };
      setProviders(setup.providers);
      /* ⚠️ Connections are read separately and are allowed to fail on their own: a visitor with no
         organisation yet has none, which is a valid state rather than an error. A single combined
         read would make "no org" indistinguishable from "the API is down". */
      if (orgId) {
        /* ⚠️ **`await`, which was missing.** `accessToken()` returns a Promise, and a Promise is
           truthy — so this sent `Authorization: Bearer [object Promise]`, the route answered 401,
           the `res.ok` branch below fell to `[]`, and **the wizard showed a connected client as
           having connected nothing**. Silent in exactly the way that matters: the screen rendered
           perfectly, and told the reader their systems were not set up. */
        const token = await accessToken();
        const res = await fetch(`/api/relay/connections?org_id=${encodeURIComponent(orgId)}`, {
          headers: token ? { authorization: `Bearer ${token}` } : {},
        });
        setConnections(res.ok ? ((await res.json()) as { connections: ConnectionView[] }).connections : []);
      } else {
        setConnections([]);
      }
    } catch {
      /* ⚠️ A FAILED read is not an empty catalogue. Rendering "no systems available" here would tell
         somebody the product supports nothing, which is the worst possible first impression and is
         also untrue. */
      setFailed('We could not load the list of systems. This is on our side — try again in a moment.');
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const connectedIds = useMemo(
    () => new Set((connections ?? []).map((c) => c.provider)),
    [connections]
  );

  const selected = useMemo(
    () => (providers ?? []).filter((p) => chosen.has(p.providerId)),
    [providers, chosen]
  );

  /** Prerequisites the reader must delegate — the thing that makes step 2 worth a step. */
  const delegated = useMemo(
    () => selected.flatMap((p) => (p.client_prerequisites ?? []).filter((q) => q.who === 'admin').map((q) => ({ p, q }))),
    [selected]
  );

  /**
   * ⚠️ **A file source can never be outstanding, and treating it as one broke the only journey a
   * client could actually finish.**
   *
   * `connectedIds` comes from the OAuth connection list. A CSV upload creates no connection — there
   * is no grant, no token, nothing to appear in it — so a file source was *permanently* missing from
   * that set and the final step told anyone who picked one:
   *
   *   > Partly connected · Still to connect: CSV file · Come back to this page when the outstanding
   *   > one is ready — your choices are saved.
   *
   * CSV is the one provider that **is** ready. So the wizard's last screen sent every client who
   * chose the working path away to wait for it, and with no OAuth app registered yet that was every
   * client who could finish at all. Found by driving the wizard to step 4 in a browser; every test
   * passed, because the fixtures that reach step 4 use a `rest` provider.
   *
   * The category error is asking "does it have a connection?" of a thing that cannot have one.
   * Presence of a *file* is not this screen's question either — the wizard's job ends at "there is
   * nothing further we need from you", and for a file source that is true the moment it is chosen.
   */
  const outstanding = useMemo(
    () => selected.filter((p) => p.transport !== 'file' && !connectedIds.has(p.providerId)),
    [selected, connectedIds]
  );

  /** Chosen file sources — reported separately, because "nothing to connect" is not "connected". */
  const fileSources = useMemo(() => selected.filter((p) => p.transport === 'file'), [selected]);

  if (failed) {
    return (
      <EmptyState
        title="We could not start setup"
        description={failed}
        action={<Button onClick={() => void load()}>Try again</Button>}
      />
    );
  }

  if (!providers || !connections) return <Skeleton variant="rect" height="12rem" />;

  const advanceLabel = ['Continue', 'I have what I need', 'Continue', 'Go to Sources'][step];
  const canAdvance = step === 0 ? chosen.size > 0 : true;
  const hint = step === 0 && chosen.size === 0 ? 'Pick at least one system, or add a CSV export instead.' : null;

  return (
    <>
      {/* ⚠️ **This screen owns its `<h1>`, because an unlisted route renders no `PageHeader`.**
          `App.tsx` derives the page heading from the nav route and skips it entirely for unlisted
          ones, so without this the document had NO h1 at all and its hierarchy started at `h2` —
          found by opening the page, invisible to eleven passing tests.
          ⚠️ **Stable across steps, deliberately.** The step headings below are `h2`s that change;
          the page's identity does not, and a screen-reader user re-orienting mid-flow should not
          find the document has been renamed under them. */}
      <PageHeader frame="none" title="Set up your systems" headingLevel={1} />
    <WizardShell
      progress={<Stepper steps={STEPS} current={step} label="Connect your systems" />}
      back={step > 0 ? <Button variant="ghost" onClick={() => setStep(step - 1)}>← Back</Button> : undefined}
      advance={
        step < 3 ? (
          <Button disabled={!canAdvance} onClick={() => setStep(step + 1)}>
            {advanceLabel}
          </Button>
        ) : (
          <Button onClick={() => { window.location.hash = '#/sources'; }}>{advanceLabel}</Button>
        )
      }
      hint={hint}
      outcome={step === 3 ? (outstanding.length === 0 ? 'success' : 'partial') : undefined}
    >
      {step === 0 ? (
        <section>
          <h2 className="web-setup__h">Which systems do you use?</h2>
          <HelpText>
            Pick the ones holding the records you want cleaned. You can add more later — nothing here
            is permanent, and connecting one is enough to start.
          </HelpText>
          <ul className="web-setup__picks">
            {providers.map((p) => {
              const on = chosen.has(p.providerId);
              return (
                <li key={p.providerId}>
                  <button
                    type="button"
                    className={`web-setup__pick${on ? ' web-setup__pick--on' : ''}`}
                    aria-pressed={on}
                    onClick={() =>
                      setChosen((prev) => {
                        const next = new Set(prev);
                        if (next.has(p.providerId)) next.delete(p.providerId);
                        else next.add(p.providerId);
                        return next;
                      })
                    }
                  >
                    <span className="web-setup__pick-name">{p.providerName}</span>
                    {connectedIds.has(p.providerId) ? <Badge variant="success">Connected</Badge> : null}
                    {/* ⚠️ Stated at the moment of choosing, not after. A system we cannot connect yet
                        is still worth picking — it tells us what to prioritise — so this informs
                        rather than blocks. */}
                    {!p.can_connect_now && p.transport !== 'file' ? (
                      <Badge variant="info">Not ready on our side</Badge>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {step === 1 ? (
        <section>
          <h2 className="web-setup__h">What each system needs from you first</h2>
          {selected.every((p) => (p.client_prerequisites ?? []).length === 0) ? (
            <Callout tone="success" title="Nothing to arrange">
              None of the systems you picked needs anything set up first. Go straight to connecting.
            </Callout>
          ) : null}

          {delegated.length > 0 ? (
            /* ⚠️ Surfaced ABOVE the per-system detail, because it is the only thing on this screen
               the reader may not be able to do themselves. Burying it in a list is how somebody
               reaches step 3, fails, and cannot tell whose problem it is. */
            /* ⚠️ "steps", not "of these" — found in a browser. `delegated` counts PREREQUISITES,
               and with one system carrying two of them the old wording read as "2 systems". */
            <Callout
              tone="warning"
              title={`${delegated.length} step${delegated.length === 1 ? '' : 's'} need${delegated.length === 1 ? 's' : ''} an administrator`}
            >
              You will need someone with admin rights in{' '}
              {[...new Set(delegated.map((d) => d.p.providerName))].join(' and ')}. It is a one-off,
              and it covers everyone in your organisation — not each person separately.
            </Callout>
          ) : null}

          {selected.map((p) => (
            <Card key={p.providerId} title={p.providerName} headingLevel={3}>
              {(p.client_prerequisites ?? []).length === 0 ? (
                <HelpText>Nothing to set up. This one connects straight away.</HelpText>
              ) : (
                <ol className="web-setup__prereqs">
                  {(p.client_prerequisites ?? []).map((q, i) => (
                    <li key={i}>
                      <span className="web-setup__prereq-title">
                        {q.title}{' '}
                        <Badge variant={q.who === 'admin' ? 'warning' : 'neutral'}>
                          {q.who === 'admin' ? 'Your admin' : 'You'}
                        </Badge>
                      </span>
                      <HelpText>{q.detail}</HelpText>
                      {q.link ? (
                        <a href={q.link} target="_blank" rel="noreferrer noopener">
                          How to do this
                        </a>
                      ) : null}
                    </li>
                  ))}
                </ol>
              )}

              {/* ⚠️ OUR limit, stated separately and in our own voice. A client blocked by our app
                  verification can do nothing about it, and listing it beside their tasks would be
                  asking them to fix our problem. */}
              {p.our_limit ? (
                <Callout tone="info" title="One thing is ours, not yours">
                  {p.our_limit}
                </Callout>
              ) : null}

              {/* ⚠️ **`serverMissing` is deliberately NOT rendered here, and that is the point of this
                  comment.** It holds our own env-var names — `HUBSPOT_CLIENT_ID` and its secret — and
                  this screen is read by a *client*. Per
                  [ADR-079](../../../../docs/platform/11-technical-architecture.md#8--architecture-decision-records)
                  Dedupr registers one OAuth app per provider and a client never holds those values,
                  so naming them here does worse than confuse: it reads as a thing they are supposed
                  to supply, and sends somebody hunting through their own HubSpot settings for a
                  field that does not exist on their side. The names belong on `admin.` where an
                  operator can act on them, and they are already there.

                  Found in a browser. Every test passed with the names on screen, because no test
                  asserts on who is *reading* a sentence. */}
              {!p.can_connect_now && p.transport !== 'file' ? (
                <>
                  <Callout tone="pending" title="Not ready on our side yet">
                    We have not finished setting this connector up yet — this is our work, not yours,
                    and there is nothing you can do to move it along. You can complete everything else
                    and connect this one when we tell you it is ready.
                  </Callout>
                </>
              ) : null}
            </Card>
          ))}
        </section>
      ) : null}

      {step === 2 ? (
        <section>
          <h2 className="web-setup__h">Connect</h2>
          {/* ⚠️ Conditional, because *"each one opens that system's own sign-in"* is false when the
              only choice is a file — there is no sign-in, no scope and no grant, and the read-access
              promise describes something that is not happening. Over-claiming what we ask for reads
              as boilerplate, which is the fastest way to make the real promise ignorable. */}
          <HelpText>
            {selected.some((p) => p.transport !== 'file')
              ? "Each one opens that system's own sign-in. We only ever ask for read access — permission to change anything is requested later, per action, the first time you approve one."
              : 'A file is read in this browser and never sent anywhere, so there is no sign-in and no access to grant.'}
          </HelpText>
          <ul className="web-setup__connects">
            {selected.map((p) => {
              const done = connectedIds.has(p.providerId);
              return (
                <li key={p.providerId} className="web-setup__connect">
                  <span className="web-setup__pick-name">{p.providerName}</span>
                  {done ? (
                    <Badge variant="success">Connected</Badge>
                  ) : p.transport === 'file' ? (
                    <Button variant="secondary" onClick={() => { window.location.hash = '#/sources'; }}>
                      Upload a file
                    </Button>
                  ) : p.can_connect_now ? (
                    /* ⚠️ **Was a top-level GET with `?org_id=`, and both halves were wrong.** The
                        route is a POST requiring an `Authorization` header (B-133), so this
                        answered 401 with a JSON problem document in the address bar; and it sent
                        `org_id` where `handleConnect` reads `org`, so even authenticated it could
                        not have named the organisation. `startConnect` is the one implementation. */
                    <Button onClick={() => void begin(p.providerId)}>
                      Connect {p.providerName}
                    </Button>
                  ) : (
                    <Badge variant="info">Not ready on our side</Badge>
                  )}
                </li>
              );
            })}
          </ul>
          <HelpText>
            Leaving this page is fine — come back to it and your progress is still here. Nothing is
            lost by waiting on an administrator.
          </HelpText>
        </section>
      ) : null}

      {step === 3 ? (
        <section>
          <h2 className="web-setup__h">
            {outstanding.length === 0 ? 'Everything is connected' : 'Partly connected'}
          </h2>
          {/* ⚠️ `partial` is a real outcome, not a failure. Somebody waiting on their Microsoft admin
              has done everything they can, and telling them they failed is both wrong and
              discouraging at exactly the wrong moment. */}
          {outstanding.length === 0 ? (
            <Callout tone="success" title="Ready">
              We will pull a first copy of your records and profile them. Nothing is changed in any of
              your systems — every change is proposed to you first.
            </Callout>
          ) : (
            <Callout tone="pending" title={`Still to connect: ${outstanding.map((p) => p.providerName).join(', ')}`}>
              Everything else is done. Come back to this page when the outstanding one is ready — your
              choices are saved.
            </Callout>
          )}

          {/* ⚠️ Reported, never counted as outstanding. A file is added whenever the client likes and
              needs nothing from us — saying so is what stops the previous screen reading as a chore
              they have left unfinished. */}
          {fileSources.length > 0 ? (
            <Callout
              tone="info"
              title={`Nothing to connect for ${fileSources.map((p) => p.providerName).join(' and ')}`}
            >
              Add a file on Sources whenever you like — it is read in this browser, so there is no
              access to grant and nothing to wait for.
            </Callout>
          ) : null}
        </section>
      ) : null}
    </WizardShell>
    </>
  );
}
